import React, { useRef, useState, useCallback } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  PanelRightOpen,
  PanelRightClose,
} from "lucide-react";
import { ProgressSpinner } from "primereact/progressspinner";
import CustomVideoPlayer from "../CustomVideoPlayer/CustomVideoPlayerWithPrevNextBtn.tsx";
import { useMediaQuery } from "@mui/material";

/* ─────────────────────────────────────────────
   Types
───────────────────────────────────────────── */
export interface VideoDialogItem {
  _id?: string;
  video_link?: string;
  poster_url?: string;
  store_name?: string;
  reason?: string;
  [key: string]: any;
}

export interface VideoDialogProps {
  visible: boolean;
  onHide: () => void;
  data: VideoDialogItem[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onLoadMore?: () => Promise<void>;
  isAllDataFetched?: boolean;
  /**
   * Render prop — VideoDialog passes its own `onSelect` handler so the
   * sidebar can trigger video resets that live inside VideoDialog.
   */
  sidebarContent?: (onSelect: (i: number) => void) => React.ReactNode;
  /** Initial sidebar visibility (default: true) */
  defaultSidebarOpen?: boolean;
  loading?: boolean;
}

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */
export default function VideoDialog({
  visible,
  onHide,
  data,
  currentIndex,
  onIndexChange,
  onLoadMore,
  isAllDataFetched = false,
  sidebarContent,
  defaultSidebarOpen = true,
  loading = false,
}: VideoDialogProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(defaultSidebarOpen);
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isTablet = useMediaQuery("(max-width: 900px)");

  const currentItem = data[currentIndex];
  const videoSrc = currentItem?.video_link || "";
  const videoHeader = currentItem?.store_name || "";
  const hasSidebar = Boolean(sidebarContent);
  const showSidebar = hasSidebar && sidebarOpen && !isMobile;

  /* ── Reset video element to start ── */
  const resetVideo = useCallback(() => {
    const v = videoRef.current;
    if (v) {
      v.currentTime = 0;
      v.load();
    }
  }, []);

  /* ── Navigate to a specific index (used by filmstrip + sidebar) ── */
  const handleSelectIndex = useCallback(
    (i: number) => {
      resetVideo();
      onIndexChange(i);
    },
    [resetVideo, onIndexChange],
  );

  const handlePrev = useCallback(async () => {
    if (currentIndex <= 0) return;
    handleSelectIndex(currentIndex - 1);
  }, [currentIndex, handleSelectIndex]);

  const handleNext = useCallback(async () => {
    const next = currentIndex + 1;
    if (next >= data.length - 1 && !isAllDataFetched && onLoadMore) {
      await onLoadMore();
    }
    if (next < data.length) {
      handleSelectIndex(next);
    }
  }, [
    currentIndex,
    data.length,
    isAllDataFetched,
    onLoadMore,
    handleSelectIndex,
  ]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex" style={{ background: "#000" }}>
      {/* ══════════════════════════════════════
          Main column: header + video + filmstrip
      ══════════════════════════════════════ */}
      <div className="flex flex-col flex-1 min-w-0 min-h-0">
        {/* ── Header ── */}
        <div
          className="
    flex
    items-center
    justify-between
    px-3
    md:px-4
    shrink-0
    border-b
    border-[rgb(var(--border-primary))]
    bg-[rgb(var(--surface-2))]
  "
          style={{
            height: "48px",
          }}
        >
          {/* LEFT */}
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="
        text-[10px]
        font-bold
        px-2
        py-0.5
        rounded-full
        shrink-0
        tabular-nums
        border
      "
              style={{
                background: "rgba(var(--color-gold-1),0.12)",
                color: "rgb(var(--color-gold-1))",
                borderColor: "rgba(var(--color-gold-1),0.22)",
              }}
            >
              {currentIndex + 1} / {data.length}
            </span>

            <span
              className="
        text-[13px]
        font-semibold
        truncate
      "
              style={{
                color: "rgb(var(--text-primary))",
              }}
            >
              {videoHeader || "Video Player"}
            </span>

            {currentItem?.reason && !isTablet && (
              <span
                className="
          text-[11px]
          truncate
        "
                style={{
                  color: "rgb(var(--text-muted))",
                }}
              >
                · {currentItem.reason}
              </span>
            )}
          </div>

          {/* RIGHT */}
          <div className="flex items-center gap-1 shrink-0">
            <NavButton
              onClick={handlePrev}
              disabled={currentIndex === 0}
              title="Previous video"
            >
              <ChevronLeft size={15} />
              {!isMobile && (
                <span className="text-[11px] font-semibold">Prev</span>
              )}
            </NavButton>

            <NavButton
              onClick={handleNext}
              disabled={currentIndex >= data.length - 1 && isAllDataFetched}
              title="Next video"
            >
              {!isMobile && (
                <span className="text-[11px] font-semibold">Next</span>
              )}

              <ChevronRight size={15} />
            </NavButton>

            {/* Divider */}
            <div
              className="w-px h-4 mx-1"
              style={{
                background: "rgb(var(--border-primary))",
              }}
            />

            {/* Sidebar Toggle */}
            {hasSidebar && !isMobile && (
              <button
                onClick={() => setSidebarOpen((v) => !v)}
                title={sidebarOpen ? "Hide panel" : "Show panel"}
                className="
          flex
          items-center
          justify-center
          w-7
          h-7
          rounded-md
          transition-all
          duration-200
        "
                style={{
                  color: sidebarOpen
                    ? "rgb(var(--color-gold-1))"
                    : "rgb(var(--text-secondary))",

                  background: sidebarOpen
                    ? "rgba(var(--color-gold-1),0.12)"
                    : "transparent",

                  border: `1px solid ${
                    sidebarOpen
                      ? "rgba(var(--color-gold-1),0.24)"
                      : "rgb(var(--border-primary))"
                  }`,
                }}
                onMouseEnter={(e) => {
                  if (!sidebarOpen) {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      "rgb(var(--surface-3))";

                    (e.currentTarget as HTMLButtonElement).style.color =
                      "rgb(var(--text-primary))";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!sidebarOpen) {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      "transparent";

                    (e.currentTarget as HTMLButtonElement).style.color =
                      "rgb(var(--text-secondary))";
                  }
                }}
              >
                {sidebarOpen ? (
                  <PanelRightClose size={14} />
                ) : (
                  <PanelRightOpen size={14} />
                )}
              </button>
            )}

            {/* Divider */}
            <div
              className="w-px h-4 mx-1"
              style={{
                background: "rgb(var(--border-primary))",
              }}
            />

            {/* Close */}
            <button
              onClick={onHide}
              title="Close"
              className="
        flex
        items-center
        justify-center
        w-7
        h-7
        rounded-md
        transition-all
        duration-200
      "
              style={{
                color: "rgb(var(--text-secondary))",
                background: "transparent",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  "rgba(239,68,68,0.12)";

                (e.currentTarget as HTMLButtonElement).style.color = "#ef4444";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  "transparent";

                (e.currentTarget as HTMLButtonElement).style.color =
                  "rgb(var(--text-secondary))";
              }}
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* ── Video body ── */}
        <div className="relative flex-1 min-h-0 bg-black">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <ProgressSpinner
                style={{ width: "52px", height: "52px" }}
                strokeWidth="3"
                animationDuration=".6s"
              />
            </div>
          ) : (
            <CustomVideoPlayer
              src={videoSrc}
              videoName={videoHeader}
              currentTime={currentTime}
              setCurrentTime={setCurrentTime}
              videoRef={videoRef}
              autoPlay
              isAllDataFetched={isAllDataFetched}
              handleNextVideo={handleNext}
              handlePrevVideo={handlePrev}
              currentVideoIndex={currentIndex}
              setCurrentVideoIndex={onIndexChange}
              Data={data}
              openDrawer={false}
            />
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════
          Sidebar — render prop receives handleSelectIndex
          so clicking an item resets + replays the video
      ══════════════════════════════════════ */}
      {showSidebar && (
        <div
          className="shrink-0 flex flex-col overflow-hidden"
          style={{
            width: "280px",
            borderLeft: "1px solid rgba(255,255,255,0.08)",
            background: "rgb(var(--surface-2))",
            transition: "width 0.25s ease",
          }}
        >
          {sidebarContent?.(handleSelectIndex)}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   NavButton
───────────────────────────────────────────── */
function NavButton({
  children,
  onClick,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="
        flex
        items-center
        gap-1
        px-2
        py-1.5
        rounded-md
        transition-all
        duration-200
        border
      "
      style={{
        color: disabled ? "rgb(var(--text-muted))" : "rgb(var(--text-primary))",

        background: disabled ? "transparent" : "rgb(var(--surface-1))",

        cursor: disabled ? "not-allowed" : "pointer",

        borderColor: "rgb(var(--border-primary))",

        opacity: disabled ? 0.45 : 1,
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          (e.currentTarget as HTMLButtonElement).style.background =
            "rgba(var(--color-gold-1),0.10)";

          (e.currentTarget as HTMLButtonElement).style.borderColor =
            "rgba(var(--color-gold-1),0.24)";

          (e.currentTarget as HTMLButtonElement).style.color =
            "rgb(var(--color-gold-1))";
        }
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = disabled
          ? "transparent"
          : "rgb(var(--surface-1))";

        (e.currentTarget as HTMLButtonElement).style.borderColor =
          "rgb(var(--border-primary))";

        (e.currentTarget as HTMLButtonElement).style.color = disabled
          ? "rgb(var(--text-muted))"
          : "rgb(var(--text-primary))";
      }}
    >
      {children}
    </button>
  );
}
